import React, { useState, useEffect, useMemo } from 'react';
import PivotTable from 'react-pivottable/PivotTable';
import 'react-pivottable/pivottable.css';
import './PivotTabelaProcessada.css';
import { aggregators as pvtAggregators } from 'react-pivottable/Utilities';
import {
    Box, Typography, Button, IconButton, Chip,
    Grid, Paper, Tooltip, Select, MenuItem, Menu,
    ListItemIcon, ListItemText, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Checkbox,
    FormControlLabel, List, ListItem, ListItemButton
} from '@mui/material';
import {
    TableChart as TableChartIcon,
    ViewColumn as ViewColumnIcon,
    ViewStream as ViewStreamIcon,
    Functions as FunctionsIcon,
    FilterAlt as FilterAltIcon,
    AddCircleOutline as AddCircleIcon,
    Close as CloseIcon,
    Description as DescriptionIcon,
    GridOn as GridOnIcon,
    Launch as LaunchIcon,
    Save as SaveIcon,
    Download as DownloadIcon,
    SwapHoriz as SwapIcon,
    ChevronRight as ChevronRightIcon,
    Search as SearchIcon,
    FilterAltOff as FilterOffIcon
} from '@mui/icons-material';

const multiAggregator = (valsConfig) => {
    return (data, rowKey, colKey) => {
        // Create an instance for each val in config
        const subAggregators = valsConfig.map(v => {
            const factory = pvtAggregators[v.op] || pvtAggregators['Sum'];
            // react-pivottable aggregators expect [attr]
            const aggInstance = factory([v.field])(data, rowKey, colKey);
            return {
                label: `${v.op} (${v.field})`,
                instance: aggInstance
            };
        });

        return {
            push(record) {
                subAggregators.forEach(s => s.instance.push(record));
            },
            value() {
                // Return a combined value. 
                // Since this is for display in a cell, we'll join them.
                return subAggregators.map(s => {
                    const val = s.instance.value();
                    const formatted = s.instance.format(val);
                    return `${s.label}: ${formatted}`;
                }).join('\n');
            },
            format(x) {
                // Formatting is already handled in value() for this multi-agg
                return x;
            },
            numInputs: 0 // We handled inputs internally
        };
    };
};

const FilterDialog = ({ open, onClose, attr, data, valueFilter, onUpdateFilter }) => {
    const [search, setSearch] = useState('');

    // Get unique values for this attribute
    const uniqueValues = useMemo(() => {
        if (!attr || !data) return [];
        const values = [...new Set(data.map(item => item[attr]))];
        return values.sort();
    }, [attr, data]);

    const filteredValues = uniqueValues.filter(v =>
        String(v).toLowerCase().includes(search.toLowerCase())
    );

    const currentFilters = valueFilter[attr] || {};

    const handleToggle = (val) => {
        const nextFilters = { ...currentFilters };
        if (nextFilters[val]) {
            delete nextFilters[val];
        } else {
            nextFilters[val] = true;
        }
        onUpdateFilter(attr, nextFilters);
    };

    const handleSelectAll = () => onUpdateFilter(attr, {});
    const handleDeselectAll = () => {
        const nextFilters = {};
        uniqueValues.forEach(v => { nextFilters[v] = true; });
        onUpdateFilter(attr, nextFilters);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { background: '#1e293b', color: 'white' } }}>
            <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 2 }}>
                Filtrar: {attr}
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Buscar valor..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ color: '#94a3b8', mr: 1 }} fontSize="small" />,
                        sx: { color: 'white', background: 'rgba(255,255,255,0.05)' }
                    }}
                    sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Button size="small" onClick={handleSelectAll} sx={{ color: '#818cf8', textTransform: 'none' }}>Selecionar Todos</Button>
                    <Button size="small" onClick={handleDeselectAll} sx={{ color: '#94a3b8', textTransform: 'none' }}>Limpar Todos</Button>
                </Box>
                <List sx={{ maxHeight: 300, overflow: 'auto', bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 1 }}>
                    {filteredValues.map(val => (
                        <ListItem key={val} disablePadding>
                            <ListItemButton onClick={() => handleToggle(val)} sx={{ py: 0.5 }}>
                                <Checkbox
                                    size="small"
                                    checked={!currentFilters[val]}
                                    sx={{ color: '#94a3b8', '&.Mui-checked': { color: '#4f46e5' } }}
                                />
                                <ListItemText primary={String(val)} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                    {filteredValues.length === 0 && (
                        <Typography sx={{ p: 2, textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>
                            Nenhum valor encontrado
                        </Typography>
                    )}
                </List>
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <Button onClick={onClose} variant="contained" sx={{ bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' } }}>
                    Concluído
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const PivotTabelaProcessada = ({ dados, defaultRows, defaultCols, defaultVals, defaultAggregator = 'Sum' }) => {
    const [config, setConfig] = useState({
        rows: defaultRows || ['tipo'],
        cols: defaultCols || ['status'],
        vals: defaultVals || [],
        filters: [],
        hiddenAttributes: [],
        hiddenFromAggregators: [],
        hiddenFromDragDrop: [],
        aggregatorName: 'Sum',
        rendererName: 'Table',
        valueFilter: {}
    });


    const [data, setData] = useState([]);
    const [allAttributes, setAllAttributes] = useState([]);
    const [metrics, setMetrics] = useState([]);
    const [dimensions, setDimensions] = useState([]);

    // Handle initial vals when data arrives
    useEffect(() => {
        if (data.length > 0 && config.vals.length === 0 && !defaultVals) {
            const firstNumeric = metrics[0] || allAttributes[0];
            if (firstNumeric) {
                setConfig(prev => ({
                    ...prev,
                    vals: [{ field: firstNumeric, op: 'Sum' }]
                }));
            }
        }
    }, [data, allAttributes, metrics]);

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedAttr, setSelectedAttr] = useState(null);

    // Filter Dialog state
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [filterAttr, setFilterAttr] = useState(null);

    useEffect(() => {
        if (dados && dados.length > 0) {
            const sanitizedData = dados.map(item => {
                const { id, _id, Razao_Social_Origem, ...rest } = item;
                return rest;
            });
            setData(sanitizedData);

            const keys = Object.keys(sanitizedData[0]);
            setAllAttributes(keys);

            // Simple heuristic to split Dimensions and Metrics
            const numericFields = keys.filter(key => {
                const val = sanitizedData[0][key];
                return typeof val === 'number' || (!isNaN(val) && val !== '');
            });
            setMetrics(numericFields);
            setDimensions(keys.filter(k => !numericFields.includes(k)));
        }
    }, [dados]);

    const handleMenuOpen = (event, attr) => {
        setAnchorEl(event.currentTarget);
        setSelectedAttr(attr);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedAttr(null);
    };

    const openFilterDialog = (attr) => {
        setFilterAttr(attr);
        setFilterDialogOpen(true);
    };

    const handleUpdateFilter = (attr, newFilters) => {
        setConfig(prev => ({
            ...prev,
            valueFilter: {
                ...prev.valueFilter,
                [attr]: newFilters
            }
        }));
    };

    // DnD State
    const [dragOverArea, setDragOverArea] = useState(null);

    // Helpers to toggle attributes between areas
    const toggleAttribute = (attr, targetArea) => {
        if (!attr || attr === 'null') return; // Guard against bad data
        const newConfig = { ...config };

        // Remove from rows/cols/filters/vals (Global move)
        newConfig.rows = newConfig.rows.filter(a => a !== attr);
        newConfig.cols = newConfig.cols.filter(a => a !== attr);
        newConfig.filters = newConfig.filters.filter(a => a !== attr);

        if (targetArea === 'rows') {
            if (!newConfig.rows.includes(attr)) newConfig.rows.push(attr);
        }
        else if (targetArea === 'cols') {
            if (!newConfig.cols.includes(attr)) newConfig.cols.push(attr);
        }
        else if (targetArea === 'vals') {
            newConfig.vals.push({ field: attr, op: 'Sum' });
        }
        else if (targetArea === 'filters') {
            if (!newConfig.filters.includes(attr)) newConfig.filters.push(attr);
        }
        else if (targetArea === 'unused') {
            newConfig.vals = newConfig.vals.filter(v => v.field !== attr);
        }

        setConfig(newConfig);
    };

    // Drag and Drop Handlers
    const handleDragStart = (e, attr) => {
        e.dataTransfer.setData('attribute', attr);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, area) => {
        e.preventDefault();
        setDragOverArea(area);
    };

    const handleDragLeave = () => {
        setDragOverArea(null);
    };

    const handleDrop = (e, targetArea) => {
        e.preventDefault();
        const attr = e.dataTransfer.getData('attribute');
        if (attr) {
            toggleAttribute(attr, targetArea);
        }
        setDragOverArea(null);
    };

    const removeVal = (index) => {
        const newConfig = { ...config };
        newConfig.vals = newConfig.vals.filter((_, i) => i !== index);
        setConfig(newConfig);
    };

    const changeValAgg = (index, newOp) => {
        const newConfig = { ...config };
        newConfig.vals[index].op = newOp;
        setConfig(newConfig);
    };

    const moveTo = (targetArea) => {
        if (!selectedAttr) return;
        toggleAttribute(selectedAttr, targetArea);
        handleMenuClose();
    };

    const swapArea = (attr) => {
        const newConfig = { ...config };
        if (newConfig.rows.includes(attr)) {
            newConfig.rows = newConfig.rows.filter(a => a !== attr);
            newConfig.cols.push(attr);
        } else if (newConfig.cols.includes(attr)) {
            newConfig.cols = newConfig.cols.filter(a => a !== attr);
            newConfig.rows.push(attr);
        }
        setConfig(newConfig);
        handleMenuClose();
    };

    const unusedDimensions = dimensions.filter(attr =>
        !config.rows.includes(attr) &&
        !config.cols.includes(attr) &&
        !config.vals.some(v => v.field === attr) && // Check if any val object uses this field
        !config.filters.includes(attr)
    );

    const unusedMetrics = metrics.filter(attr =>
        !config.rows.includes(attr) &&
        !config.cols.includes(attr) &&
        // We allow multiple vals for the same metric, so don't filter them out here
        !config.filters.includes(attr)
    );

    const aggregators = ['Sum', 'Average', 'Count', 'Minimum', 'Maximum', 'Median', 'Sum as Fraction of Total'];

    return (
        <Box className="premium-pivot-root">
            {/* TOOLBAR HEADER */}
            <Box className="pivot-header">
                <Box>
                    <Typography variant="h5" className="pivot-title">Tabela Dinâmica</Typography>
                    <Typography variant="caption" className="pivot-subtitle">
                        Análise multidimensional com rastreabilidade total — atualiza automaticamente
                    </Typography>
                </Box>
                <Box className="pivot-actions">
                    <Button startIcon={<GridOnIcon />} className="action-btn">Excel</Button>
                    <Button startIcon={<DescriptionIcon />} className="action-btn">CSV</Button>
                    <Button startIcon={<SaveIcon />} className="action-btn">Salvar Visão</Button>
                    <Button
                        startIcon={<LaunchIcon />}
                        className="action-btn primary"
                        onClick={() => window.open('/auditoria/pivot', '_blank')}
                    >
                        Nova Aba
                    </Button>
                </Box>
            </Box>

            {/* THE 4 TILES */}
            <Grid container spacing={2} className="pivot-tiles-grid">
                <Grid item xs={12} sm={3}>
                    <Paper
                        className={`axis-card rows-card ${dragOverArea === 'rows' ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, 'rows')}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, 'rows')}
                    >
                        <Box className="card-header">
                            <ViewStreamIcon fontSize="small" />
                            <Typography>LINHAS</Typography>
                        </Box>
                        <Box className="card-content">
                            {config.rows.map(attr => (
                                <Box key={attr} sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                                    <Chip
                                        label={attr}
                                        onDelete={() => toggleAttribute(attr, 'unused')}
                                        className="pvt-chip active"
                                        sx={{ pr: 4 }}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, attr)}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => swapArea(attr)}
                                        sx={{ position: 'absolute', right: 28, color: 'rgba(255,255,255,0.7)', p: '2px' }}
                                    >
                                        <SwapIcon fontSize="inherit" />
                                    </IconButton>
                                </Box>
                            ))}
                            {config.rows.length === 0 && <Typography variant="caption" className="placeholder">Arraste dimensões aqui</Typography>}
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Paper
                        className={`axis-card cols-card ${dragOverArea === 'cols' ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, 'cols')}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, 'cols')}
                    >
                        <Box className="card-header">
                            <ViewColumnIcon fontSize="small" />
                            <Typography>COLUNAS</Typography>
                        </Box>
                        <Box className="card-content">
                            {config.cols.map(attr => (
                                <Box key={attr} sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                                    <Chip
                                        label={attr}
                                        onDelete={() => toggleAttribute(attr, 'unused')}
                                        className="pvt-chip active"
                                        sx={{ pr: 4 }}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, attr)}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => swapArea(attr)}
                                        sx={{ position: 'absolute', right: 28, color: 'rgba(255,255,255,0.7)', p: '2px' }}
                                    >
                                        <SwapIcon fontSize="inherit" />
                                    </IconButton>
                                </Box>
                            ))}
                            {config.cols.length === 0 && <Typography variant="caption" className="placeholder">Arraste aqui</Typography>}
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Paper
                        className={`axis-card vals-card ${dragOverArea === 'vals' ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, 'vals')}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, 'vals')}
                    >
                        <Box className="card-header">
                            <FunctionsIcon fontSize="small" />
                            <Typography>VALORES</Typography>
                        </Box>
                        <Box className="card-content" sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
                            {config.vals.map((v, idx) => (
                                <Box key={`${v.field}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    <Chip
                                        label={v.field}
                                        onDelete={() => removeVal(idx)}
                                        className="pvt-chip active"
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, v.field)}
                                    />
                                    <Select
                                        value={v.op}
                                        onChange={(e) => changeValAgg(idx, e.target.value)}
                                        size="small"
                                        className="val-select"
                                        sx={{ height: 28, fontSize: '0.75rem', flexGrow: 1 }}
                                    >
                                        {aggregators.map(agg => (
                                            <MenuItem key={agg} value={agg} sx={{ fontSize: '0.75rem' }}>{agg}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            ))}
                            {config.vals.length === 0 && <Typography variant="caption" className="placeholder">Clique em métricas</Typography>}
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Paper
                        className={`axis-card filters-card ${dragOverArea === 'filters' ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, 'filters')}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, 'filters')}
                    >
                        <Box className="card-header">
                            <FilterAltIcon fontSize="small" />
                            <Typography>FILTROS</Typography>
                        </Box>
                        <Box className="card-content">
                            {config.filters.map(attr => (
                                <Chip
                                    key={attr}
                                    label={attr}
                                    onClick={() => openFilterDialog(attr)}
                                    onDelete={() => toggleAttribute(attr, 'unused')}
                                    className="pvt-chip active"
                                    color="error"
                                    variant="outlined"
                                    sx={{ borderColor: 'var(--filters-accent)', color: 'white' }}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, attr)}
                                />
                            ))}
                            {config.filters.length === 0 && <Typography variant="caption" className="placeholder">Toque no menu para filtrar</Typography>}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* DIMENSIONS BAR */}
            <Box className="attribute-bar-section">
                <Box className="bar-label">
                    <DescriptionIcon fontSize="inherit" />
                    <Typography variant="caption" sx={{ ml: 1 }}>DIMENSÕES</Typography>
                </Box>
                <Box className="bar-pills">
                    {unusedDimensions.map(attr => (
                        <Chip
                            key={attr}
                            label={attr}
                            onClick={(e) => handleMenuOpen(e, attr)}
                            className="pvt-chip inactive"
                            icon={<AddCircleIcon style={{ color: 'inherit', fontSize: '1rem' }} />}
                            draggable
                            onDragStart={(e) => handleDragStart(e, attr)}
                        />
                    ))}
                    {unusedDimensions.length === 0 && <Typography variant="caption" className="placeholder">Sem dimensões extras</Typography>}
                </Box>
            </Box>

            {/* METRICS BAR */}
            <Box className="attribute-bar-section metrics">
                <Box className="bar-label">
                    <FunctionsIcon fontSize="inherit" />
                    <Typography variant="caption" sx={{ ml: 1 }}>MÉTRICAS</Typography>
                </Box>
                <Box className="bar-pills">
                    {unusedMetrics.map(attr => (
                        <Chip
                            key={attr}
                            label={attr}
                            onClick={(e) => handleMenuOpen(e, attr)}
                            variant="outlined"
                            className="pvt-chip metric"
                            icon={<FunctionsIcon style={{ color: 'inherit', fontSize: '1rem' }} />}
                            draggable
                            onDragStart={(e) => handleDragStart(e, attr)}
                        />
                    ))}
                    {unusedMetrics.length === 0 && <Typography variant="caption" className="placeholder">Todas métricas em uso</Typography>}
                </Box>
            </Box>

            {/* TABLE OUTPUT */}
            <Box className="pivot-output-area">
                <Box className="output-scroller" sx={{ '& .pvtVal': { whiteSpace: 'pre-wrap', lineHeight: 1.4 } }}>
                    {data.length > 0 ? (
                        <PivotTable
                            data={data}
                            {...config}
                            vals={[]} // We handle fields inside multiAggregator
                            aggregators={{ 'Multi': () => multiAggregator(config.vals) }}
                            aggregatorName="Multi"
                            onChange={c => setConfig(prev => ({ ...prev, ...c }))}
                        />
                    ) : (
                        <Typography variant="caption">Processando dados...</Typography>
                    )}
                </Box>
            </Box>

            {/* INTERACTION MENU */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        background: '#1e293b',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.1)',
                        minWidth: 180
                    }
                }}
            >
                <Typography sx={{ px: 2, py: 1, fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8' }}>
                    ADICIONAR A:
                </Typography>
                <MenuItem onClick={() => moveTo('rows')}>
                    <ListItemIcon><ViewStreamIcon fontSize="small" sx={{ color: '#818cf8' }} /></ListItemIcon>
                    <ListItemText primary="Linhas" />
                    <ChevronRightIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                </MenuItem>
                <MenuItem onClick={() => moveTo('cols')}>
                    <ListItemIcon><ViewColumnIcon fontSize="small" sx={{ color: '#fbbf24' }} /></ListItemIcon>
                    <ListItemText primary="Colunas" />
                    <ChevronRightIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                </MenuItem>
                <MenuItem onClick={() => moveTo('vals')}>
                    <ListItemIcon><FunctionsIcon fontSize="small" sx={{ color: '#10b981' }} /></ListItemIcon>
                    <ListItemText primary="Valores" />
                    <ChevronRightIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                </MenuItem>
                <MenuItem onClick={() => moveTo('filters')}>
                    <ListItemIcon><FilterAltIcon fontSize="small" sx={{ color: '#f87171' }} /></ListItemIcon>
                    <ListItemText primary="Filtros" />
                    <ChevronRightIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                </MenuItem>
            </Menu>

            {/* FILTER DIALOG */}
            <FilterDialog
                open={filterDialogOpen}
                onClose={() => setFilterDialogOpen(false)}
                attr={filterAttr}
                data={data}
                valueFilter={config.valueFilter}
                onUpdateFilter={handleUpdateFilter}
            />
        </Box>
    );
};

export default PivotTabelaProcessada;
